/**
 * Sparkline: tiny trend line for a metric tile, no axes.
 * Props: label (accessible title, e.g. "Orders, last 7 days"), values, formatValue, labels? (one per value, for the hidden
 *        table; default 1..n), width? (px, default 96), height? (px, default 28), loading?, className?.
 * The last value is marked with a dot. Fewer than one value renders an em dash.
 */
import { useTranslation } from 'react-i18next';
import { ChartState, DataTable } from './chartKit';
import { colorOf } from './chartUtils';

export interface SparklineProps {
    label: string;
    values: number[];
    formatValue: (n: number) => string;
    labels?: string[];
    width?: number;
    height?: number;
    loading?: boolean;
    className?: string;
}

export const Sparkline = ({ label, values, formatValue, labels, width = 96, height = 28, loading, className }: SparklineProps) => {
    const { t } = useTranslation('charts');
    const n = values.length;
    const max = values.reduce((m, v) => Math.max(m, v), 0);
    const min = values.reduce((m, v) => Math.min(m, v), max);
    const pad = 3;
    const x = (i: number) => (n > 1 ? pad + (i * (width - 2 * pad)) / (n - 1) : width / 2);
    const y = (v: number) => (max === min ? height / 2 : height - pad - ((v - min) / (max - min)) * (height - 2 * pad));
    const last = values[n - 1] ?? 0;
    return (
        <div className={className} style={{ width }}>
            <ChartState loading={loading} empty={n === 0} height={height}>
                <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false" className="block">
                    {n > 1 && (
                        <path d={`M${values.map((v, i) => `${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join('L')}`} fill="none" stroke={colorOf(0)} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                    )}
                    <circle cx={x(n - 1)} cy={y(last)} r="2.5" fill={colorOf(0)} />
                </svg>
            </ChartState>
            <DataTable caption={label} headers={[t('item'), t('value')]} rows={values.map((v, i) => [labels?.[i] ?? String(i + 1), formatValue(v)])} />
        </div>
    );
};
